/**
 * Timecode parsing / formatting for the audio player.
 *
 * Users need to jump straight to a given minute of a call instead of dragging
 * the progress bar looking for it, so the elapsed time is editable and accepts
 * the shorthands people actually type.
 */

/** Formats a number of seconds as `m:ss`. Minutes are not wrapped into hours. */
export const formatTimecode = (timeInSeconds) => {
  const safe = Number.isFinite(timeInSeconds) && timeInSeconds > 0 ? timeInSeconds : 0;

  const minutes = Math.floor(safe / 60);
  const seconds = String(Math.floor(safe % 60)).padStart(2, "0");

  return `${minutes}:${seconds}`;
};

/**
 * Parses a user-typed timecode into seconds.
 *
 * Accepted forms (`:` and `.` are interchangeable as separators):
 *   "12"       -> minute 12          (720s)  — a bare number is a minute
 *   "12:30"    -> 12min 30s          (750s)
 *   "1:05:30"  -> 1h 05min 30s      (3930s)
 *
 * Returns the number of seconds, clamped to `durationInSeconds` when that is
 * known, or `null` when the input cannot be understood.
 */
export const parseTimecode = (raw, durationInSeconds) => {
  if (typeof raw !== "string") return null;

  const input = raw.trim().replace(/\s/g, "");
  if (!input) return null;

  const parts = input.split(/[:.]/);
  if (parts.length > 3) return null;
  if (parts.some((part) => !/^\d+$/.test(part))) return null;

  let seconds;

  if (parts.length === 1) {
    // A bare number is a minute: "12" means "jump to minute 12".
    seconds = Number(parts[0]) * 60;
  } else {
    const secondsPart = Number(parts[parts.length - 1]);
    const minutesPart = Number(parts[parts.length - 2]);
    const hoursPart = parts.length === 3 ? Number(parts[0]) : 0;

    if (secondsPart > 59) return null;
    if (parts.length === 3 && minutesPart > 59) return null;

    seconds = hoursPart * 3600 + minutesPart * 60 + secondsPart;
  }

  if (!Number.isFinite(seconds)) return null;

  if (Number.isFinite(durationInSeconds) && durationInSeconds > 0) {
    return Math.min(seconds, durationInSeconds);
  }

  return seconds;
};
